import type { OperationalException } from "./staff.js";
import { operationalExceptionNeedsAttention } from "@wmf/shared/operationalExceptions";

/**
 * Sorting the exception feed into the two things it actually contains.
 *
 * FastAPI decides which rows still need attention. Uncertain operations stay
 * in the queue; terminal failed attempts belong in history. Their common
 * origin as operational rows does not make them the same kind of work.
 *
 * Two passes, in this order:
 *
 * **Already actionable above wins.** An exception naming a record the queue is
 * already offering a decision on is the same thing said twice, so it is
 * dropped. Matched on `entity_id` against the ids on screen — string equality
 * against opaque identifiers, with no assumption about how `entity_type` is
 * spelled. If FastAPI ever keys these differently the match simply stops
 * finding anything and the grouping below still holds; the failure mode is a
 * duplicate reappearing, not a genuine failure disappearing.
 *
 * FastAPI supplies needs_attention for queue counts, grouping and styling.
 * One shared fallback keeps the Hub badge and web list aligned while an older
 * backend is still deployed.
 */
export interface SortedExceptions {
  /** An incomplete or uncertain operation needing attention/reconciliation. */
  waiting: OperationalException[];
  /** Terminal failed attempts retained as history. */
  failed: OperationalException[];
}

export function exceptionLabel(
  item: Pick<OperationalException, "code" | "severity" | "needs_attention">,
): string {
  return operationalExceptionNeedsAttention(item) ? "Needs attention" : "History";
}

export function exceptionGuidance(code: string): string | null {
  if (["booking_reconciliation_required", "booking_mutation_outcome_unknown"].includes(code)) {
    return "Booking and credit outcome may be uncertain. The original payment and ledger need checking. Inspect Amelia and WMF before retrying; do not take another payment or issue another credit.";
  }
  if (code === "booking_override_configuration_failed") {
    return "Check whether Amelia created the appointment and whether a credit was used before attempting another booking. The original payment record is unchanged by this warning.";
  }
  if (code === "coupon_email_delivery_failed") {
    return "The legacy block remains recorded; email delivery did not change its entitlement. Check the recipient and mail provider before resending. Do not issue another block.";
  }
  return null;
}

export function sortExceptions(
  items: readonly OperationalException[],
  alreadyActionable: ReadonlySet<string>,
): SortedExceptions {
  const sorted: SortedExceptions = { waiting: [], failed: [] };

  for (const item of items) {
    if (alreadyActionable.has(item.entity_id)) continue;
    if (exceptionLabel(item) === "Needs attention") sorted.waiting.push(item);
    else sorted.failed.push(item);
  }

  return sorted;
}
